import clsx from "clsx";
import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { Search } from "@bfc-software/pallet";
import {
  useTranslation,
  TRANSLATION_NAMESPACE,
} from "@bfc-software/bfc-web-ui/i18n";

import { useTaskFilters, validateTaskFiltersSearch } from "./-data/filters";
import { SelectorFilter } from "./-components/selector-filter";
import styles from "./tasks.module.css";

/**
 * Outbound Tasks: the shell the two tabs share.
 *
 * The tabs are two routes rather than two panels, because they are two questions
 * with almost nothing in common behind them. "Task plans" is the manager's
 * release decision - what is queued up and the button that puts it on the floor.
 * "All tasks" is the floor as it stands, ignoring which wave anything came from.
 * They read different endpoints, hold different state, and fail independently;
 * the only thing they genuinely share is what this file draws.
 *
 * So this route owns the heading, the two search boxes and the tab bar, and
 * nothing else. The searches live here rather than in each tab because searching
 * for a task is the same narrowing in both, and duplicating them per tab is what
 * lets the two drift out of sync.
 *
 * They reach the tabs through this route's *search params* rather than through a
 * context: a filtered view is something a manager sends to someone else, and a
 * view that lives in memory is one they have to describe over the phone instead.
 * The layout owning the params is also what lets both tabs read them with the
 * same hook - `-data/filters.ts` - whichever one is mounted.
 *
 * A pathless `_layout` segment, so the tabs are `/tasks/plans` and `/tasks/all`
 * and neither inherits an extra level of URL for the privilege of sharing a
 * header.
 */
export const Route = createFileRoute(
  "/$lang/_layout/orgs/$org/_layout/outbound/whs/$location/_layout/tasks/_layout",
)({
  validateSearch: validateTaskFiltersSearch,
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();
  const { t } = useTranslation({
    lang: params.lang,
    namespace: TRANSLATION_NAMESPACE.outbound,
  });

  const { search, setSearch } = useTaskFilters();

  return (
    <div className={styles.page} data-test-role="outbound-tasks">
      <div className={styles.header}>
        <div>
          <h1 className={styles.heading}>{t("tasks.heading")}</h1>
          <p className={styles.subheading}>{t("tasks.subheading")}</p>
        </div>
        {/*
          Two boxes rather than one that guesses. A task id and a route id are
          both bare numbers here, so a single box cannot tell which was typed -
          and a manager holding a paper route sheet knows exactly which they have.
        */}
        <div className={styles.searches}>
          <Search
            className={styles.search}
            aria-label={t("tasks.search.taskId.ariaLabel")}
            placeholder={t("tasks.search.taskId.placeholder")}
            value={search.taskQuery ?? ""}
            onChange={(value) => setSearch({ taskQuery: String(value) })}
            onClear={() => setSearch({ taskQuery: undefined })}
          />
          <Search
            className={styles.search}
            aria-label={t("tasks.search.routeId.ariaLabel")}
            placeholder={t("tasks.search.routeId.placeholder")}
            value={search.routeQuery ?? ""}
            onChange={(value) => setSearch({ routeQuery: String(value) })}
            onClear={() => setSearch({ routeQuery: undefined })}
          />
        </div>
      </div>

      <div className={styles.tabBar}>
        {/*
          Links rather than tab buttons: the two tabs are routes, so the browser's
          back button and a pasted URL both have to land where the tab does. The
          underline is still the selected state - react-aria's `Tabs` would have
          duplicated the routing it already has.
        */}
        <nav className={styles.tabList} aria-label={t("tasks.tabs.ariaLabel")}>
          <Link
            to="/$lang/orgs/$org/outbound/whs/$location/tasks/plans"
            params={params}
            // The filters survive the tab change, which is the point of having
            // them in the URL: the same narrowing asked of the other question.
            search={true}
            inactiveProps={{ className: styles.tab }}
            activeProps={{ className: clsx(styles.tab, styles.tabActive) }}
            data-test-role="tab-plans"
          >
            {t("tasks.tabs.plans")}
          </Link>
          <Link
            to="/$lang/orgs/$org/outbound/whs/$location/tasks/all"
            params={params}
            search={true}
            inactiveProps={{ className: styles.tab }}
            activeProps={{ className: clsx(styles.tab, styles.tabActive) }}
            data-test-role="tab-all"
          >
            {t("tasks.tabs.all")}
          </Link>
        </nav>

        {/*
          Shared by both tabs, same as the searches above: `selector` is a URL
          param `useTaskFilters` owns, not either tab's own state. It names a
          person, and it narrows both tabs on the wire - the same
          `selectorAppUserExtIds` param on each listing. The tasks nobody holds
          are the All tasks tab's `Unassigned` chip rather than a value of this
          box.
        */}
        <SelectorFilter params={params} t={t} />
      </div>

      <Outlet />
    </div>
  );
}
